import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DateRange = ({ onFilteredDataChange }) => {
  const [dateRange, setDateRange] = useState("This Month");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [compareWith, setCompareWith] = useState(1);
  const [customPeriod, setCustomPeriod] = useState("");
  const initializedRef = useRef(false);

  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const getRangeDates = React.useCallback((range, offset = 0) => {
    let adjustedMonth = currentMonth + offset;

    // Handle year rollover for month offset
    const date = new Date(currentYear, adjustedMonth, 1);
    const resultYear = date.getFullYear();
    const resultMonth = date.getMonth();

    switch (range) {
      case "This Month":
        return [
          new Date(resultYear, resultMonth, 1),
          new Date(resultYear, resultMonth + 1, 0),
        ];
      case "Last Month":
        return [
          new Date(resultYear, resultMonth - 1, 1),
          new Date(resultYear, resultMonth, 0),
        ];
      case "Last Quarter":
        // offset is already -3*i for last quarter
        const quarterStartMonth = resultMonth - 2;
        return [
          new Date(resultYear, quarterStartMonth, 1),
          new Date(resultYear, resultMonth + 1, 0),
        ];
      case "This Year":
        return [new Date(currentYear + offset, 0, 1), new Date(currentYear + offset, 11, 31)];
      case "Last Year":
        return [
          new Date(currentYear + offset - 1, 0, 1),
          new Date(currentYear + offset - 1, 11, 31),
        ];
      case "Custom":
        if (customEndDate) {
          const endDateWithTime = new Date(customEndDate);
          endDateWithTime.setHours(23, 59, 59, 999);
          return [customStartDate, endDateWithTime];
        }
        return [customStartDate, customEndDate];
      default:
        return [];
    }
  }, [currentMonth, currentYear, customEndDate, customStartDate]);

  const filterData = React.useCallback(() => {
    if (
      dateRange === "Custom" &&
      (!customStartDate || !customEndDate || customStartDate > customEndDate)
    ) {
      alert("Please select a valid custom date range.");
      return;
    }

    const ranges = [];
    for (let i = 0; i < compareWith; i++) {
      const offset = dateRange === "Last Quarter" ? -3 * i : -i;
      const offsetRange = getRangeDates(dateRange, offset);
      ranges.push({
        startDate: offsetRange[0],
        endDate: offsetRange[1],
      });
    }

    onFilteredDataChange({
      ranges,
    });
  }, [dateRange, customStartDate, customEndDate, compareWith, onFilteredDataChange, getRangeDates]);

  // Trigger default filtering on mount - only once
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      filterData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (compareWith > 1 && dateRange === "Custom") {
      setDateRange("This Month");
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  }, [compareWith, dateRange]);

  return (
    <div className="p-4 bg-white rounded-lg shadow-md flex flex-wrap gap-4 items-center mb-2 justify-between">
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <label htmlFor="dateRange" className="text-lg font-semibold">
          Date Range:
        </label>
        <select
          id="dateRange"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-4 py-2 border rounded-md"
        >
          <option value="This Month">This Month</option>
          <option value="Last Month">Last Month</option>
          <option value="Last Quarter">Last Quarter</option>
          <option value="This Year">This Year</option>
          <option value="Last Year">Last Year</option>
          {compareWith === 1 && <option value="Custom">Custom</option>}
        </select>
        {dateRange === "Custom" && compareWith === 1 && (
          <div className="flex gap-4">
            <DatePicker
              selected={customStartDate}
              onChange={setCustomStartDate}
              placeholderText="Start Date"
              className="border rounded-md px-4 py-2"
            />
            <DatePicker
              selected={customEndDate}
              onChange={setCustomEndDate}
              placeholderText="End Date"
              className="border rounded-md px-4 py-2"
            />
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <label htmlFor="compareWith" className="text-lg font-semibold">
          Compare With:
        </label>
        <select
          id="compareWith"
          value={compareWith}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "custom") {
              setCompareWith("custom");
            } else {
              setCompareWith(parseInt(value, 10));
              setCustomPeriod("");
            }
          }}
          className="px-4 py-2 border rounded-md"
        >
          <option value={1}>1 Period</option>
          <option value={2}>2 Periods</option>
          <option value={3}>3 Periods</option>
          <option value={4}>4 Periods</option>
          <option value="custom">Custom Periods</option>
        </select>
        {(compareWith === "custom" || customPeriod !== "") && (
          <input
            type="number"
            value={customPeriod}
            onChange={(e) => {
              const period = Math.max(
                0,
                Math.min(12, parseInt(e.target.value, 10))
              );
              setCustomPeriod(period);
              setCompareWith(period);
            }}
            className="px-4 py-2 rounded-md w-40"
            placeholder="X should be <13"
            min="5"
            max="12"
          />
        )}
      </div>

      <button
        onClick={filterData}
        className="mt-4 sm:mt-0 px-4 py-2 bg-blue-500 text-white rounded-md ml-auto"
      >
        Update
      </button>
    </div>
  );
};

export default DateRange;
